import { WidgetEnv, ActionBarFormButton, ActionBarScheduleButton } from "@xapp/stentor-chat-widget";
import React, { FC, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { ChatConfigContext, Config, defaultServerUrl, useConnectionInfo } from "../../config";
import { useOpenUrlCallback } from "../../hooks/useOpenUrlCallback";
import { MessageMiddleware } from "../../middlewares";
import { executeAction, reset, sendFile, sendMessage, sendTyping } from "../../store/actions";
import { writeMessage } from "../../store/actions/writeMessage";
import { CONNECTION_STATUS_TYPE, ChatDetail, ChatMsgDetail } from "../../store/ChatAction";
import { ChatState } from "../../store/ChatState";
import { checkSessionExpiration } from "../../utils/checkSessionExpiration";
import { getConfigurableMessages } from "../../utils/configurableMessages";
import { useChatDispatch } from "../../utils/useChatDispatch";
import { useChatServerDispatch } from "../../utils/useChatServerDispatch";
import useExternalScript from "../../utils/useExternalScript";
import useGreeting from "../../utils/useGreeting";
import { ChatServerOptions } from "../../xapp/ChatServer";
import { ChatMessageRequest, ChatServerActionLink, ChatServerList } from "../../xapp/ChatServerMessage";
import { ChatServerContext, useChatServer } from "../../xapp/XappChat";
import { convertToListDisplay } from "../../utils/responseToMessage";
import { ActionBar } from "../ActionBar";
import { ChatButton } from "../ChatButton";
import { ChatFooter } from "../ChatFooter";
import { FormModal } from "../FormModal";
import { MessageList } from "../MessageList";
import { ServerOffline } from "../ServerOffline";
import { ChatHeader } from "../ChatHeader";
import { WidgetStylesheet } from "../WidgetStylesheet";
import { ErrorOverlay } from "../ErrorOverlay";
import { WsButton } from "../WsButton";
import { WS_BUTTON_DISMISS_ANIMATION_DURATION } from "../WsButton/constants";
import "./ChatWidget.scss";
import RefreshConfirmationModal from "./ModalContent";
import setVisualStatus from "../../store/actions/setVisualStatus";
import setSessionId from "../../store/actions/setSessionId";
import { log, err, preloadFormWidgetScript, openFormWidgetWhenReady } from "../../utils";
import StaticMessagesChatWidgetContainer from "./StaticMessagesChatWidgetContainer";

/**
 * Debounce delay for typing indicator in milliseconds.
 * After this delay of inactivity, the "stop typing" event will be sent.
 */
const TYPING_INDICATOR_DEBOUNCE_MS = 2000;

/**
 * Check if we're in development mode. Uses typeof to prevent TypeScript
 * errors after Rollup replaces process.env.NODE_ENV with a literal string.
 */
const __DEV__ = typeof process !== "undefined" &&
    typeof process.env !== "undefined" &&
    process.env.NODE_ENV !== "production";

/**
 * Preview message structure for preview mode.
 * Allows rendering sample messages with suggestions and lists.
 *
 * **Important:** At least one of `displayText` or `html` should be provided for
 * the message to render content. Messages without both fields will render empty.
 */
export interface PreviewMessage {
    /**
     * The main text to display in the message bubble.
     * If neither displayText nor html is provided, the message will render empty.
     */
    readonly displayText?: string;

    /**
     * HTML content to render in the message bubble.
     * Takes precedence over displayText if both are provided.
     *
     * **Security Warning:** This field renders raw HTML without sanitization.
     * Only use with trusted content. If preview messages may come from untrusted
     * sources (user input, external APIs), sanitize the HTML using a library
     * like DOMPurify before passing it to this field.
     *
     * @example
     * ```tsx
     * // Safe: hardcoded content
     * { html: "<strong>Welcome!</strong>" }
     *
     * // Unsafe without sanitization: external content
     * // import DOMPurify from 'dompurify';
     * // { html: DOMPurify.sanitize(untrustedContent) }
     * ```
     */
    readonly html?: string;

    /**
     * Suggestion chips to display below the message.
     * Suggestions without URLs will use "#" as a fallback to prevent navigation.
     */
    readonly suggestions?: ReadonlyArray<{
        readonly title: string;
        /**
         * URL to navigate to when the suggestion is clicked.
         * Defaults to "#" if not provided.
         */
        readonly url?: string;
    }>;

    /**
     * List or carousel to display with the message.
     * Note: The `description` field maps to `subTitle` in the internal representation.
     */
    readonly list?: {
        readonly type: "LIST" | "CAROUSEL";
        readonly title?: string;
        readonly items: ReadonlyArray<{
            readonly title: string;
            /**
             * Description text shown below the title.
             * Internally mapped to `subTitle` in ChatServerListItem.
             */
            readonly description?: string;
            readonly url?: string;
            readonly imageUrl?: string;
            /**
             * When true, hides the URL text and external link icon while
             * still allowing the item to be clickable.
             */
            readonly hideUrl?: boolean;
        }>;
    };
}

/**
 * Props for the ChatWidget component.
 *
 * This interface defines all available configuration options for the chat widget,
 * including display modes, configuration loading strategies, and event callbacks.
 */
export interface ChatWidgetProps {
    /**
     * Enable pre-chat form before starting the conversation.
     * When enabled, users must fill out a form (name, email, etc.) before accessing chat.
     *
     * @default false
     * @example
     * ```tsx
     * <ChatWidget preChatFormEnabled={true} config={myConfig} />
     * ```
     */
    readonly preChatFormEnabled?: boolean;

    /**
     * Widget environment configuration containing server connection details, theme, and behavior settings.
     *
     * This is the primary configuration object that defines how the widget connects to your backend,
     * how it appears visually, and how it behaves. Can be provided directly or via the `getConfig` callback.
     *
     * **Note:** If both `config` and `getConfig` are provided, `getConfig` takes precedence.
     *
     * @see WidgetEnv for the full configuration schema
     * @example
     * ```tsx
     * const config: WidgetEnv = {
     *   connection: {
     *     serverUrl: 'wss://chat.example.com',
     *     type: 'websocketraw'
     *   },
     *   channelId: 'support-channel',
     *   theme: {
     *     header: { background: { color: '#007AFF' } }
     *   }
     * };
     *
     * <ChatWidget config={config} />
     * ```
     */
    readonly config?: WidgetEnv;

    /**
     * Display mode for the widget.
     *
     * - **`'normal'`** (default): Floating chat button that expands into a chat window.
     *   User can minimize/maximize. Best for desktop websites.
     *
     * - **`'docked'`**: Always visible, docked to the side or bottom of the screen.
     *   Cannot be minimized. Best for mobile apps and WebViews.
     *
     * - **`'static'`**: Embedded inline in the page content, takes up fixed space.
     *   Cannot be minimized or moved. Best for dedicated chat pages.
     *
     * @default 'normal'
     * @example
     * ```tsx
     * // Docked mode for mobile
     * <ChatWidget config={config} mode="docked" />
     *
     * // Static mode for dedicated chat page
     * <ChatWidget config={config} mode="static" />
     * ```
     */
    readonly mode?: ChatWidgetMode;

    /**
     * Enable preview mode for displaying static sample messages without server connections.
     *
     * When `true`, the widget renders in a read-only state with:
     * - No server connection attempts
     * - Static messages from `previewMessages` prop
     * - Disabled input and interactions
     *
     * Can be combined with any `mode` for different visual layouts:
     * - `mode="docked" preview` - Docked layout with preview data
     * - `mode="static" preview` - Static layout with preview data
     * - `mode="normal" preview` - Floating layout with preview data
     *
     * @default false
     * @example
     * ```tsx
     * // Docked preview for settings page
     * <ChatWidget
     *   mode="docked"
     *   preview
     *   config={themeConfig}
     *   previewMessages={sampleMessages}
     * />
     * ```
     */
    readonly preview?: boolean;

    /**
     * Custom middleware for processing and transforming chat messages before rendering.
     *
     * Middleware allows you to intercept messages and render custom components for specific
     * message types or modify message content. Useful for adding custom UI elements,
     * formatting, or handling special message types.
     *
     * @see MessageMiddleware
     * @example
     * ```tsx
     * const customMiddleware = (next) => (props) => {
     *   const { msg } = props;
     *
     *   // Handle custom message type
     *   if (msg.type === 'SPECIAL_CARD') {
     *     return <CustomCard data={msg.data} />;
     *   }
     *
     *   // Pass to next middleware
     *   return next(props);
     * };
     *
     * <ChatWidget
     *   config={config}
     *   messageMiddleware={customMiddleware}
     * />
     * ```
     */
    readonly messageMiddleware?: MessageMiddleware;

    /**
     * Callback fired when the connection status changes.
     *
     * Use this to monitor the widget's connection state and display custom UI or
     * handle connection issues. Connection status changes throughout the widget lifecycle
     * as it connects, disconnects, reconnects, or encounters errors.
     *
     * @param status - Current connection status
     *   - `'online'`: Successfully connected to server
     *   - `'offline'`: Disconnected from server
     *   - `'pending'`: Connecting or reconnecting (loading state)
     *
     * @example
     * ```tsx
     * const [status, setStatus] = useState('pending');
     *
     * <ChatWidget
     *   config={config}
     *   onConnectionStatusChange={(newStatus) => {
     *     setStatus(newStatus);
     *     if (newStatus === 'offline') {
     *       alert('Chat is currently unavailable');
     *     }
     *   }}
     * />
     * ```
     */
    onConnectionStatusChange?(status: CONNECTION_STATUS_TYPE): void;

    /**
     * Optional async callback to load configuration dynamically.
     *
     * When provided, the widget will wait for this Promise to resolve before initializing.
     * This prevents race conditions where the widget attempts to connect before configuration
     * is available. Particularly useful for:
     *
     * - **React Native apps**: Where props may propagate with delays across the native bridge
     * - **API-based config**: When configuration needs to be fetched from a server
     * - **Dynamic config**: When config depends on user authentication or other async operations
     * - **Native modules**: When waiting for React Native native modules to initialize
     *
     * While the Promise is pending, the widget displays a loading spinner. If the Promise
     * rejects, an error message is shown to the user.
     *
     * **Important:** If both `config` and `getConfig` are provided, `getConfig` takes precedence
     * and the `config` prop is ignored.
     *
     * @returns Promise that resolves to the WidgetEnv configuration
     * @throws Error if configuration cannot be loaded (will be displayed to user)
     *
     * @example
     * ```tsx
     * // Basic usage - fetch from API
     * <ChatWidget
     *   getConfig={async () => {
     *     const response = await fetch('/api/chat-config');
     *     return response.json();
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // React Native - wait for native module
     * import { NativeModules } from 'react-native';
     *
     * <ChatWidget
     *   getConfig={async () => {
     *     await NativeModules.ChatModule.initialize();
     *     return await NativeModules.ChatModule.getConfig();
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // Wait for authentication with error handling
     * <ChatWidget
     *   getConfig={async () => {
     *     // Wait for auth
     *     while (!window.auth?.user) {
     *       await new Promise(resolve => setTimeout(resolve, 100));
     *     }
     *
     *     // Fetch config with user context
     *     try {
     *       const config = await fetchConfigForUser(window.auth.user);
     *       return config;
     *     } catch (error) {
     *       throw new Error('Failed to load chat configuration. Please try again.');
     *     }
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // With timeout protection
     * <ChatWidget
     *   getConfig={async () => {
     *     const timeout = new Promise((_, reject) =>
     *       setTimeout(() => reject(new Error('Config load timeout')), 10000)
     *     );
     *
     *     const configPromise = fetch('/api/config').then(r => r.json());
     *
     *     return Promise.race([configPromise, timeout]);
     *   }}
     * />
     * ```
     */
    getConfig?(): Promise<WidgetEnv>;

    /**
     * Disable automatic scrolling behavior when messages change.
     *
     * When enabled, prevents the widget from scrolling to the bottom when new messages
     * arrive or when the component mounts. This is particularly useful for:
     *
     * - **Embedded preview contexts**: When the widget is used as a static preview in settings pages
     * - **Multi-widget pages**: When multiple widgets are on the same page
     * - **Custom scroll control**: When you want to manage scrolling behavior externally
     *
     * @default false
     * @example
     * ```tsx
     * // Use in an embedded preview
     * <StaticMessagesChatWidgetContainer
     *   messages={previewMessages}
     *   config={config}
     *   disableAutoScroll={true}
     * />
     * ```
     */
    readonly disableAutoScroll?: boolean;

    /**
     * Preview messages to display when mode="preview".
     *
     * Array of sample messages to show in preview mode. Each message can include
     * text, HTML, suggestion chips, and lists. Only applies when mode is set to "preview".
     *
     * @example
     * ```tsx
     * <ChatWidget
     *   mode="preview"
     *   config={widgetConfig}
     *   previewMessages={[
     *     {
     *       displayText: "Hello! How can I help you today?",
     *       suggestions: [
     *         { title: "Get a Quote" },
     *         { title: "Schedule Service", url: "#schedule" }
     *       ],
     *       list: {
     *         type: "LIST",
     *         items: [
     *           { title: "Our Services", description: "View all services", url: "#services" },
     *           { title: "Contact Us", description: "Get in touch", url: "#contact" }
     *         ]
     *       }
     *     }
     *   ]}
     * />
     * ```
     */
    readonly previewMessages?: readonly PreviewMessage[];
}

/**
 * Props for ChatWidgetWrapper component.
 * Extends ChatWidgetProps with the same interface.
 */
export interface ChatWidgetWrapperProps extends ChatWidgetProps {}

/**
 * Display modes for the chat widget (visual layout).
 *
 * - **`'normal'`**: Default floating button mode - chat button appears as a floating action button,
 *   clicking it opens the chat window. User can minimize/maximize the window.
 *
 * - **`'docked'`**: Always-visible docked mode - widget is permanently visible and docked to the
 *   side or bottom of the screen. Cannot be minimized. Ideal for mobile apps and WebViews.
 *
 * - **`'static'`**: Static embedded mode - widget is embedded inline in the page content as a
 *   fixed element. Cannot be minimized or repositioned. Ideal for dedicated chat pages.
 *
 * - **`'preview'`**: (Deprecated) Shorthand for `mode="static" preview={true}`.
 *   Prefer using the `preview` prop with any mode for clearer intent.
 *
 * @see {@link ChatWidgetProps.preview} for enabling preview mode with any layout.
 */
export type ChatWidgetMode = "normal" | "docked" | "static" | "preview";

/**
 * Converts preview messages to ChatDetail format for rendering.
 * This allows preview mode to use the existing message rendering infrastructure.
 *
 * @param messages - Array of preview messages to convert
 * @param config - Widget configuration containing avatarUrl and botName
 * @returns Array of ChatDetail objects suitable for MessageList rendering
 */
function convertPreviewMessagesToChatDetails(messages: readonly PreviewMessage[], config?: WidgetEnv): ChatDetail[] {
    const timestamp = Date.now();

    return messages.map((msg, index) => {
        // Validate that message has content - log warning in development
        if (__DEV__ && !msg.displayText && !msg.html) {
            console.warn(
                "[ChatWidget Preview] Message at index %d has no displayText or html content and will render empty.",
                index
            );
        }

        const chatDetail: ChatMsgDetail = {
            type: "chat.msg",
            user: {
                nick: "agent:robot",
                display_name: config?.botName || "Assistant",
                avatarPath: config?.avatarUrl,
            },
            timestamp: timestamp + index,
            msg: {
                text: msg.displayText,
                html: msg.html,
                // Use "#" as fallback for suggestions without URLs to prevent navigation
                options: msg.suggestions?.map((s): ChatServerActionLink => ({
                    label: s.title,
                    actionUrl: s.url || "#"
                })),
                // Store list in both formats:
                // - 'list': ChatServerList format for interface consistency and testing
                // - 'displays': Display format required by ListMiddleware for rendering
                list: msg.list ? {
                    type: msg.list.type,
                    title: msg.list.title,
                    items: msg.list.items.map(item => ({
                        title: item.title,
                        subTitle: item.description,
                        url: item.url,
                        imageUrl: item.imageUrl,
                        hideUrl: item.hideUrl,
                    }))
                } : undefined,
                displays: msg.list ? [convertToListDisplay({
                    type: msg.list.type,
                    title: msg.list.title,
                    items: msg.list.items.map(item => ({
                        title: item.title,
                        subTitle: item.description,
                        url: item.url,
                        imageUrl: item.imageUrl,
                        hideUrl: item.hideUrl,
                    }))
                })] : undefined
            }
        };
        return chatDetail;
    });
}

/**
 * Preview mode component - renders a static chat widget without server connection.
 * Separated to avoid violating React hooks rules in the main wrapper.
 */
const ChatWidgetPreview: FC<ChatWidgetWrapperProps> = (props) => {
    const mode = props.mode ?? "preview";

    // Memoize preview message conversion to avoid recalculating on every render
    const previewChatDetails = useMemo(() => {
        return convertPreviewMessagesToChatDetails(props.previewMessages || [], props.config);
    }, [props.previewMessages, props.config]);

    return (
        <StaticMessagesChatWidgetContainer
            messages={previewChatDetails}
            mode={mode}
            config={props.config}
            disableAutoScroll={props.disableAutoScroll}
            preview={true}
        />
    );
};

/**
 * Main wrapper component that handles config loading and server connection.
 */
const ChatWidgetConnected: FC<ChatWidgetWrapperProps> = (props) => {
    const mode = props.mode ?? "normal";
    const [rawConfig, setRawConfig] = useState<WidgetEnv | undefined>(props.config);
    const [configLoading, setConfigLoading] = useState<boolean>(!!props.getConfig);
    const [configError, setConfigError] = useState<Error | undefined>();

    // Load config from callback if provided
    useEffect(() => {
        let cancelled = false;

        if (props.getConfig) {
            setConfigLoading(true);
            setConfigError(undefined);

            props
                .getConfig()
                .then((config) => {
                    if (!cancelled) {
                        setRawConfig(config);
                        setConfigLoading(false);
                        log("[ChatWidget] Config loaded from getConfig callback");
                    }
                })
                .catch((error) => {
                    if (!cancelled) {
                        setConfigError(error);
                        setConfigLoading(false);
                        err(`[ChatWidget] Failed to load config: ${error.message}`);
                    }
                });
        } else if (props.config) {
            // If no callback, use the config prop directly
            setRawConfig(props.config);
            setConfigLoading(false);
        }

        return () => {
            cancelled = true;
        };
        // Only depend on getConfig and config - not the entire props object to avoid infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.getConfig, props.config]);

    const connection = useConnectionInfo(rawConfig);

    const config = useMemo<Config>(
        () => ({
            ...rawConfig,
            connection,
            assetUrl: connection?.serverUrl ?? defaultServerUrl,
            env: rawConfig,
        }),
        [connection, rawConfig]
    );

    const token = useSelector<ChatState, string | null>((state) => state.connection.token);

    const options = useMemo<ChatServerOptions>(() => {
        let configurableMessages = getConfigurableMessages();

        if (
            rawConfig?.configurableMessages &&
            Array.isArray(rawConfig.configurableMessages.items) &&
            rawConfig.configurableMessages.items.length > 0
        ) {
            configurableMessages = rawConfig.configurableMessages;
        }

        return {
            token,
            bot: {
                nick: "Bot",
                displayName: rawConfig?.botName,
                avatarPath: rawConfig?.avatarUrl,
            },
            configurableMessages,
            hooks: rawConfig?.hooks,
            env: rawConfig,
        };
    }, [
        token,
        rawConfig?.botName,
        rawConfig?.avatarUrl,
        rawConfig?.configurableMessages,
        rawConfig?.hooks,
    ]);

    // Only create chat server when config is ready (not loading and no error)
    const chatServer = useChatServer(
        configLoading || configError ? null : connection,
        configLoading || configError ? null : options
    );

    // Determine mode class for loading/error states (mode already declared above)
    const modeClass = `widget-container--${mode}`;

    // Show loading state while config is being fetched
    if (configLoading) {
        return (
            <div className={`widget-container widget-container--loading ${modeClass}`}>
                <div className="xa-spinner-container visible">
                    <div className="xa-spinner" />
                </div>
            </div>
        );
    }

    // Show error state if config failed to load
    if (configError) {
        return (
            <div className={`widget-container widget-container--error ${modeClass}`}>
                <div className="widget-error-message">
                    Failed to load chat configuration: {configError.message}
                </div>
            </div>
        );
    }

    return (
        <ChatConfigContext.Provider value={config}>
            <ChatServerContext.Provider value={chatServer}>
                <ChatWidgetUI {...props} config={rawConfig} />
            </ChatServerContext.Provider>
        </ChatConfigContext.Provider>
    );
};

/**
 * Inner ChatWidget component that renders the actual UI.
 * Exported for use by StaticChatWidgetContainer to avoid infinite loops.
 */
export const ChatWidgetUI: FC<ChatWidgetProps> = (props) => {
    const innerDispatch = useChatDispatch();
    const dispatch = useChatServerDispatch();

    // From Redux
    const chatState = useSelector<ChatState, ChatState>((state) => state);

    // Refresh modalReference
    const modalRef = useRef<HTMLDivElement>({} as HTMLDivElement);

    const mode = props.mode ?? "normal";
    const dockedMode = mode === "docked";
    const staticMode = mode === "static";
    // Preview mode can be enabled via `preview` prop (recommended) or legacy `mode="preview"`
    const previewMode = props.preview === true || mode === "preview";
    const modeClass = `widget-container--${mode}`;

    const canRefresh = props.config.header?.actions?.refresh;
    // can't minimize in docked mode, static mode, or preview mode.
    const canMinimize = !dockedMode && !staticMode && !previewMode && props.config.header?.actions?.minimize;
    log(
        `docked: ${dockedMode} static: ${staticMode} preview: ${previewMode} minimized: ${props.config.header?.actions?.minimize}`
    );

    let canCancel: boolean;
    // To preserve legacy behavior, cancel needs a little more checks
    if (typeof props.config.header?.actions?.cancel === "boolean") {
        canCancel = !dockedMode && !staticMode && !previewMode && props.config.header?.actions?.cancel;
    } else {
        canCancel = !dockedMode && !staticMode && !previewMode;
    }

    // For backward compatibility. Note: the action will create the actual "visuals" object" (this is a copy).
    if (!chatState.visuals) {
        chatState.visuals = {};
    }

    // Our state - pull from storage
    const [visible, setVisibleState] = useState<boolean>(
        (!canMinimize && !canCancel) ||
            // !!get("visible") ||
            chatState.visuals.visible ||
            (props.config?.autoOpenOnWidth &&
                window.matchMedia(`(min-width: ${props.config?.autoOpenOnWidth})`).matches)
    );
    const [_typing, setTypingState] = useState<boolean>(false); // false initially - state kept for potential external observers

    // Ref to track typing state for use in timeout callbacks
    const typingRef = useRef<boolean>(false);

    // Timeout ref for debouncing "stop typing" events
    const stopTypingTimeoutRef = useRef<number | null>(null);

    const chatServer = useContext(ChatServerContext);

    const patternsConfig = props.config?.autoOpenOnPattern?.patterns;

    const currentUrl = window.location.href;

    const patternExist = patternsConfig && patternsConfig.length > 0;

    const patternMatches = patternsConfig?.some((pattern) => currentUrl.includes(pattern));

    const configWidth = props.config?.autoOpenOnPattern?.minimumWidth;

    // eslint-disable-next-line no-restricted-globals
    const currentWidth = screen.width;

    const setVisible = useCallback(
        (newVisible: boolean) => {
            if (staticMode || previewMode) {
                return;
            }
            log(`setVisible: ${newVisible}`);
            setVisibleState(newVisible);
            innerDispatch(
                setVisualStatus({
                    visible: newVisible,
                })
            );
        },
        [innerDispatch, staticMode, previewMode]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const body = document.getElementsByTagName("body")[0];
            body.tabIndex = -1;
            if (event.key === "Escape") {
                body.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        if (
            checkSessionExpiration(
                chatState?.sessionExpiration || props.config?.sessionExpiration,
                chatState?.chats[chatState.chats.length - 1]?.timestamp,
                chatState?.lastTimestamp
            )
        ) {
            innerDispatch(setSessionId(undefined));
            innerDispatch(reset());
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            // Cleanup typing timeout on unmount
            if (stopTypingTimeoutRef.current) {
                clearTimeout(stopTypingTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabVisible(!document.hidden);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        // Too early?
        if (!chatServer) {
            log("Tab visibility changed but no chatServer yet");
            return;
        }

        if (!isTabVisible) {
            log("Tab is HIDDEN - calling sleep()");
            chatServer.sleep();
        } else {
            log("Tab is VISIBLE - calling wakeup()");
            chatServer.wakeup();
        }
    }, [chatServer, innerDispatch, isTabVisible]);

    // Handle beforeunload to notify router when user is leaving
    useEffect(() => {
        if (!chatServer) {
            return undefined;
        }

        const handleBeforeUnload = () => {
            log("beforeunload - notifying router of user disconnecting");
            if (chatServer.notifyDisconnecting) {
                chatServer.notifyDisconnecting();
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [chatServer]);

    useEffect(() => {
        // For reopen widget after move on same window
        // if (get("opened")) {
        //     setVisible(true);
        // }

        if (chatState.visuals.opened) {
            setVisible(true);
        } else {
            if (mode === "normal") {
                setVisible(false);
            }
        }

        if (patternExist && patternMatches) {
            setVisible(true);
        }

        if (currentWidth < +configWidth) {
            setVisible(false);
        }
    }, [
        currentWidth,
        patternExist,
        patternMatches,
        configWidth,
        setVisible,
        chatState.visuals.opened,
        mode,
    ]);

    const stopTyping = useCallback(() => {
        if (!typingRef.current) return;

        // Clear the timeout if it exists
        if (stopTypingTimeoutRef.current) {
            clearTimeout(stopTypingTimeoutRef.current);
            stopTypingTimeoutRef.current = null;
        }

        dispatch(sendTyping(false));
        setTypingState(false);
        typingRef.current = false;
    }, [dispatch]);

    const handleOnChange = useCallback(() => {
        // Send "typing" event only on first keystroke
        if (!typingRef.current) {
            dispatch(sendTyping(true));
            setTypingState(true);
            typingRef.current = true;
        }

        // Clear any existing timeout
        if (stopTypingTimeoutRef.current) {
            clearTimeout(stopTypingTimeoutRef.current);
        }

        // Set a new timeout to send "stop typing" after inactivity
        stopTypingTimeoutRef.current = window.setTimeout(() => {
            stopTyping();
        }, TYPING_INDICATOR_DEBOUNCE_MS);
    }, [dispatch, stopTyping]);

    function handleSendMessage(msg: ChatMessageRequest) {
        // Don't allow sending messages in preview mode
        if (previewMode) {
            if (__DEV__) {
                console.warn("[ChatWidget Preview] Sending messages is disabled in preview mode.");
            }
            return;
        }
        dispatch(sendMessage(msg));
    }

    function handleWriteMessage(msg: ChatMessageRequest) {
        // Don't allow writing messages in preview mode
        if (previewMode) {
            if (__DEV__) {
                console.warn("[ChatWidget Preview] Writing messages is disabled in preview mode.");
            }
            return;
        }
        innerDispatch(writeMessage(msg.msg, msg.user));
    }

    const handleOpenUrl = useOpenUrlCallback();

    function handleOnSubmit(rawInput: string) {
        // Don't allow any actions in preview mode
        if (previewMode) {
            if (__DEV__) {
                console.warn("[ChatWidget Preview] Input submission is disabled in preview mode.");
            }
            return;
        }

        // Don't allow visitor to send msg if not chatting
        if (chatState.accountStatus === "offline" && !chatState.isChatting) return;

        // Don't send empty messages
        if (!rawInput) return;

        // Immediately stop typing
        stopTyping();

        chatServer.flush();

        dispatch(executeAction(rawInput));
    }

    function handleFileUpload(event: React.DragEvent<HTMLInputElement>) {
        event.preventDefault();

        // Don't allow file uploads in preview mode
        if (previewMode) {
            if (__DEV__) {
                console.warn("[ChatWidget Preview] File uploads are disabled in preview mode.");
            }
            return;
        }

        // Only send the first file dropped on input
        const file = event?.dataTransfer?.files[0];

        if (!file) {
            return;
        }

        dispatch(sendFile(file));
    }

    function getVisibilityClass() {
        if (visible) {
            return "visible";
        }
        // Skip hide animation on initial page load when widget was already closed
        // This prevents the "flash" of the close animation running on mount
        if (!chatState.visuals?.hasInteracted) {
            return "widget-container--initial-hidden";
        }
        return "";
    }

    function openRestartConfirmationModal() {
        // Interesting, this call the modalRef of the restart modal
        modalRef.current.style.display = "block";
    }

    function closeRestartConfirmationModal() {
        modalRef.current.style.display = "none";
    }

    function handleReset() {
        innerDispatch(reset());
    }

    function handleRestartClick() {
        if (config.header.actions.refreshShowConfirmation) {
            // show the modal
            openRestartConfirmationModal();
            // we will close when
        } else {
            handleReset();
        }
    }

    function handleRestartModalCloseClick() {
        closeRestartConfirmationModal();
    }

    /** Called when minimize button is clicked */
    function handleMinimizeClick() {
        innerDispatch(
            setVisualStatus({
                opened: false,
                hasInteracted: true,
            })
        );
        setVisible(false);
    }

    /** Called when cancel is clicked */
    function handleCancelClick() {
        // First reset to clear all state
        innerDispatch(reset());
        // Then set hasInteracted to prevent CTA from showing after cancel
        innerDispatch(
            setVisualStatus({
                opened: false,
                hasInteracted: true,
            })
        );
        setVisible(false);
    }

    function chatButtonOnClick() {
        innerDispatch(
            setVisualStatus({
                opened: true,
                hasInteracted: true,
            })
        );
        setVisible(true);
        setTimeout(() => {
            document.getElementById("chatWidgetInput").focus();
        }, 100);
    }

    function handleCtaDismiss() {
        innerDispatch(
            setVisualStatus({
                hasInteracted: true,
            })
        );
    }

    // Action Bar state and handlers
    const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
    const [activeFormConfig, setActiveFormConfig] = useState<ActionBarFormButton | ActionBarScheduleButton | null>(null);

    const handleFormButtonClick = useCallback((formButton: ActionBarFormButton | ActionBarScheduleButton) => {
        // If form-widget is configured, use its openForm API
        // The form-widget script should already be pre-loaded
        const options = formButton.preselectChipLabel
            ? { preselectChipLabel: formButton.preselectChipLabel }
            : undefined;

        openFormWidgetWhenReady(options).then((opened) => {
            if (!opened) {
                // Fallback to modal if form-widget isn't available
                log("Form widget not available, showing modal fallback");
                setActiveFormConfig(formButton);
                setFormModalOpen(true);
            }
        });
    }, []);

    const handleFormModalClose = useCallback(() => {
        setFormModalOpen(false);
        setActiveFormConfig(null);
    }, []);

    function handleWsButtonPress(buttonId: string) {
        log(`WS Button pressed: ${buttonId}`);

        // If the button has spinning behavior, update state to show spinner
        if (chatState.wsButton?.pressBehavior === "spinning") {
            // Update the button state to show it's pressed
            innerDispatch({
                type: "ws_button_display",
                detail: {
                    ...chatState.wsButton,
                    isPressed: true,
                    timestamp: +new Date()
                }
            });
        }

        // Send the button pressed event to the server
        if (chatServer && chatServer.sendButtonPressed) {
            chatServer.sendButtonPressed(buttonId).catch((error) => {
                err(`Failed to send button press: ${error}`);
                // Reset button state on error
                if (chatState.wsButton) {
                    innerDispatch({
                        type: "ws_button_display",
                        detail: {
                            ...chatState.wsButton,
                            isPressed: false,
                            timestamp: +new Date()
                        }
                    });
                }
            });
        }

        // If the button has disabled behavior, animate out then dismiss
        if (chatState.wsButton?.pressBehavior === "disabled") {
            // First set dismissing state to trigger animation
            innerDispatch({
                type: "ws_button_display",
                detail: {
                    ...chatState.wsButton,
                    isDismissing: true,
                    timestamp: +new Date()
                }
            });

            // Then actually dismiss after animation completes
            setTimeout(() => {
                innerDispatch({
                    type: "ws_button_dismiss",
                    detail: {
                        id: buttonId,
                        timestamp: +new Date()
                    }
                });
            }, WS_BUTTON_DISMISS_ANIMATION_DURATION);
        }
    }

    const isOffline = chatState.accountStatus === "offline" && !chatState.isChatting;
    const messages = chatState && chatState.chats;

    log(`Rendering - accountStatus: "${chatState.accountStatus}", connectionStatus: "${chatState.connection.connectionStatus}"`);

    const { config, onConnectionStatusChange } = props;
    const actionBarEnabled = config?.actionBar?.enabled === true;

    // Disable greeting in preview mode
    useGreeting(!isOffline && !props.preChatFormEnabled && visible && !previewMode);

    const connectionStatus = chatState.connection.connectionStatus;
    useEffect(() => {
        if (onConnectionStatusChange) {
            onConnectionStatusChange(connectionStatus);
        }
    }, [connectionStatus, onConnectionStatusChange]);

    useExternalScript(props.config?.middlewareUrl);

    // Pre-load form-widget script if formWidgetKey is provided
    useEffect(() => {
        if (config?.formWidgetKey) {
            preloadFormWidgetScript(config.formWidgetKey);
        }
    }, [config?.formWidgetKey]);

    // Set body attribute when actionBar is enabled (used by CSS to hide form-widget side button on mobile)
    // Uses reference counting to handle multiple widget instances
    useEffect(() => {
        if (actionBarEnabled) {
            // Increment reference count
            const currentCount = parseInt(document.body.getAttribute("data-xapp-action-bar-count") || "0", 10);
            document.body.setAttribute("data-xapp-action-bar-count", String(currentCount + 1));
            document.body.setAttribute("data-xapp-action-bar-enabled", "true");
        }
        return () => {
            if (actionBarEnabled) {
                // Decrement reference count
                const currentCount = parseInt(document.body.getAttribute("data-xapp-action-bar-count") || "0", 10);
                const newCount = Math.max(0, currentCount - 1);
                if (newCount === 0) {
                    document.body.removeAttribute("data-xapp-action-bar-enabled");
                    document.body.removeAttribute("data-xapp-action-bar-count");
                } else {
                    document.body.setAttribute("data-xapp-action-bar-count", String(newCount));
                }
            }
        };
    }, [actionBarEnabled]);

    // This is a pseudo agent. It represent's the widget (shown in the header avatar for instance)
    const widgetAgent = chatState.agents["agent:robot"]?.user ||
        config?.agent || {
            nick: "agent:robot",
            avatarPath: config.avatarUrl,
            display_name: "Agent",
        };

    return (
        <>
            <div className={`widget-container ${modeClass} ${getVisibilityClass()}${actionBarEnabled ? " widget-container--with-action-bar" : ""}`}>
                <WidgetStylesheet theme={config?.theme} />
                <ChatHeader
                    accountStatus={chatState.accountStatus}
                    refreshOnClick={handleRestartClick}
                    minimizeOnClick={handleMinimizeClick}
                    cancelOnClick={handleCancelClick}
                    agent={widgetAgent}
                    canRefresh={canRefresh}
                    canMinimize={canMinimize}
                    canCancel={canCancel}
                    config={config?.header}
                    menuConfig={config.menu}
                    debugMode={props.config?.connection?.mcp?.debugMode}
                    onSubmit={handleOnSubmit}
                />
                <MessageList
                    visible={visible}
                    queuePosition={chatState.queuePosition}
                    isChatting={chatState.isChatting}
                    isOffline={isOffline}
                    messages={messages}
                    agents={chatState.agents}
                    agent={config?.agent}
                    lastRatingRequestTimestamp={chatState.lastRatingRequestTimestamp}
                    hasRating={chatState.hasRating}
                    visitorId={chatState.visitorId}
                    hasWsButton={!!chatState.wsButton}
                    messageMiddleware={props.messageMiddleware}
                    textTypingStatusEnabled={props.config?.typingStatus?.textTypingStatusEnabled}
                    disableAutoScroll={props.disableAutoScroll}
                    onSend={handleSendMessage}
                    onWrite={handleWriteMessage}
                    onOpenUrl={handleOpenUrl}
                    minimizeOnClick={handleMinimizeClick}
                />
                <div
                    className={`xa-spinner-container ${
                        visible && connectionStatus === "pending" ? "visible" : ""
                    }`}>
                    <div className="xa-spinner" />
                </div>
                {connectionStatus === "offline" && <ServerOffline />}
                {chatState.wsButton && visible && (
                    <WsButton
                        button={chatState.wsButton}
                        onPress={handleWsButtonPress}
                    />
                )}
                <ChatFooter
                    isAdmin={config?.isAdmin}
                    isChatting={chatState.isChatting}
                    placeholder={config?.input?.placeholder}
                    sendButtonIcon={config?.footer?.sendButton?.icon}
                    sendButtonIconHover={config?.footer?.sendButton?.iconHover}
                    sendButtonIconDisabled={config?.footer?.sendButton?.iconDisabled}
                    visible={visible}
                    hasWsButton={!!chatState.wsButton}
                    menuConfig={props.config?.menu}
                    footerConfig={props.config?.footer}
                    inputConfig={props.config?.input}
                    disabled={previewMode}
                    onChange={handleOnChange}
                    onSubmit={handleOnSubmit}
                    onFileUpload={handleFileUpload}
                />
                <div className="restartModal" ref={modalRef} onClick={handleRestartModalCloseClick}>
                    <RefreshConfirmationModal
                        onClose={handleRestartModalCloseClick}
                        onReset={handleReset}
                    />
                </div>
            </div>
            {actionBarEnabled && config.actionBar ? (
                <>
                    <ActionBar
                        config={{
                            ...config.actionBar,
                            // Use actionBar.cta if set, otherwise fall back to widget-level cta
                            cta: config.actionBar.cta ?? config.cta,
                        }}
                        visible={visible}
                        chatDisabled={config.disabled}
                        hasUserInteracted={chatState.visuals?.hasInteracted}
                        onChatClick={chatButtonOnClick}
                        onChatMinimize={handleMinimizeClick}
                        onFormClick={handleFormButtonClick}
                        onCtaDismiss={handleCtaDismiss}
                    />
                    {formModalOpen && activeFormConfig && (
                        <FormModal
                            config={activeFormConfig}
                            widgetEnv={config}
                            onClose={handleFormModalClose}
                        />
                    )}
                </>
            ) : (
                <ChatButton
                    addClass={visible ? "visible" : ""}
                    onClick={chatButtonOnClick}
                    config={config?.cta}
                    imageUrl={config?.chatButton?.imageUrl}
                    visible={visible}
                    hasInteracted={chatState.visuals?.hasInteracted}
                    onCtaDismiss={handleCtaDismiss}
                />
            )}
            <ErrorOverlay enableErrorOverlay={config?.enableErrorOverlay} />
        </>
    );
};

/**
 * Top-level wrapper that dispatches between preview mode and connected mode.
 * This separation ensures React hooks rules are not violated.
 */
const ChatWidgetWrapper: FC<ChatWidgetWrapperProps> = (props) => {
    const rawMode = props.mode ?? "normal";
    // Support both `preview` prop and legacy `mode="preview"`
    const isPreviewMode = props.preview === true || rawMode === "preview";
    // For visual layout, use "static" when mode is "preview" (legacy), otherwise use the actual mode
    const visualMode = rawMode === "preview" ? "static" : rawMode;

    if (isPreviewMode) {
        return <ChatWidgetPreview {...props} mode={visualMode} />;
    }

    return <ChatWidgetConnected {...props} />;
};

export default ChatWidgetWrapper;
